import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useRoutePath } from "../../../hooks/useRoutePath";
import { AssetStatusArchiving, AssetStatusArchived, AssetStatusRestoring } from "../AssetStatus";
import { MediaImage } from "../components";
import { Box, Checkbox, Grid, Stack, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { IconAi, IconDrm, IconLink, IconLock, IconNew, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import { alpha } from "@mui/material";
import menuService from "@services/menuService";
import { useSelector } from "react-redux";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import { useLocation } from "react-router";
import { ARCHIVED_ASSET_STATUS, ASSET_PREVIEW_STATUS, ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE } from "@constants";
import { CircledIcon } from "@components_v2/icon";
import AssetProxyStatus, { getAssetProxyStatus, isAssetProxyStatusNoteworthy } from "@components_v2/asset-proxy-status";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const AssetTableCard = ({
    asset,
    onChangeSelectItem,
    selectedItems,
    checkable,
    thumbnailCrop,
    isProxyVideoEnabled,
    onClickName,
}) => {
    const v = asset;
    const routePath = useRoutePath();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);

    const project = useSelector(({ project }) => project);
    const stage = useSelector(({ stage }) => stage);
    const [isShowImagePreviewModal, setIsShowImagePreviewModal] = useState(false);

    // 프록시 추출 상태(표시 전용 파생). 영상 자산이 아니거나 프록시가 없으면 null (DAM-457)
    const proxyStatus = useMemo(() => getAssetProxyStatus(v), [v]);
    // 아카이브 계열 상태는 같은 좌하단 자리를 쓰므로 겹치지 않게 우선권을 준다.
    const isArchiveRelated = [
        ARCHIVED_ASSET_STATUS.ARCHIVED,
        ARCHIVED_ASSET_STATUS.ARCHIVING,
        ARCHIVED_ASSET_STATUS.RESTORING,
    ].includes(v.archiveStatus);

    const handleShowImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(true);
    }, [isShowImagePreviewModal]);

    const handleCloseImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(false);
    }, [isShowImagePreviewModal]);

    const assetDetailPath = useMemo(() => {
        if (onClickName) return onClickName(asset);
        else
            return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                asset.project?.id
            }`;
    }, [spaceId, projectId, asset]);

    return (
        <div className="list-content">
            <Grid container alignItems={"center"} flexWrap={"nowrap"}>
                {checkable && (
                    // TODO: assets.isActiveView 에 따라서 안보이게 되므로 해당 라인 주석
                    <Checkbox
                        color={"greySecondary"}
                        checked={!!selectedItems.find((item) => item.id === v.id)}
                        onChange={() => onChangeSelectItem(v)}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                )}
                <MediaImageWrapper
                    status={v.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR)?.[0]?.status}
                    sx={{
                        position: `relative`,
                        minWidth: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        width: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        height: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        borderRadius: `4px`,
                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                        ml: 1,
                        mr: 1.5,
                    }}
                >
                    <MediaImage
                        variant={"table"}
                        errorIconProps={{ position: "left", size: 16 }}
                        asset={v}
                        thumbnailCrop={thumbnailCrop}
                        isShowImagePreviewModal={isShowImagePreviewModal}
                        handleShowImagePreviewModal={handleShowImagePreviewModal}
                        handleCloseImagePreviewModal={handleCloseImagePreviewModal}
                    />
                    {v.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVED && (
                        <AssetStatusArchived containerType={"archive"} status={v.archiveStatus} variant={"table"} />
                    )}
                    {v.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVING && <AssetStatusArchiving />}
                    {v.archiveStatus === ARCHIVED_ASSET_STATUS.RESTORING && <AssetStatusRestoring />}
                    {/* 프록시 추출 상태(DAM-457) - 카드 뷰와 같은 규칙으로 썸네일 좌하단에 얹는다.
                        40px 썸네일을 가리지 않도록 NEW 배지처럼 모서리에 살짝 걸치게 배치.
                        아카이브 계열 상태가 있으면 그쪽이 우선. */}
                    {isProxyVideoEnabled && !isArchiveRelated && isAssetProxyStatusNoteworthy(proxyStatus) && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: theme.spacing(-0.25),
                                bottom: theme.spacing(-0.25),
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <AssetProxyStatus status={proxyStatus.status} size={"sm"} />
                        </Box>
                    )}
                    {countDaysBetweentDates({ fromDate: asset.createdAt }) < 7 && (
                        <Box
                            sx={{
                                position: "absolute",
                                right: theme.spacing(-0.25),
                                bottom: theme.spacing(-0.25),
                                color: theme.palette.error.main,
                            }}
                        >
                            <IconNew size={34} viewBox={"0 0 34 16"} style={{ height: "16px" }} />
                        </Box>
                    )}
                </MediaImageWrapper>
                <Stack
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                    }}
                >
                    <Grid container alignItems={"center"} flexWrap={"nowrap"} sx={{ overflow: `hidden` }} width={1}>
                        <AssetTypeFlag size={"small"} type={asset.mediaType} />
                        <Grid item xs sx={{ textOverflow: `ellipsis`, overflow: `hidden` }}>
                            <EllipsisTypography variant={"body1_500"} tooltipTitle={asset.name} noWrap>
                                <Link
                                    to={assetDetailPath}
                                    style={{ color: `inherit`, ":hover": { textDecoration: `none` } }}
                                >
                                    {asset.name}
                                </Link>
                            </EllipsisTypography>
                        </Grid>
                    </Grid>
                    <Grid container alignItems={"center"} gap={0.75} mt={1} flexWrap={"nowrap"}>
                        <Grid item xs>
                            <Grid container>
                                <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                    {asset.extensions ? asset.extensions : "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Stack direction={"row"} gap={0.5} sx={{ alignItems: "center" }}>
                                {asset.isConnected ? <IconLink size={16} /> : ""}
                                {asset.myFavorite ? <IconStar size={16} htmlColor={theme.palette.etc.orange500} /> : ""}
                                {asset.hasDubbing && (
                                    <CircledIcon size={20} color={theme.palette.etc.purple400}>
                                        <IconVoice size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.encryptions?.indexOf("DRM") > -1 && (
                                    <CircledIcon size={20} color={theme.palette.warning.main}>
                                        <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.isAnalyzed && (
                                    <CircledIcon size={20} color={theme.palette.success.light}>
                                        <IconAi size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.isOwnerLocked && (
                                    <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                        <IconLock size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Grid>
        </div>
    );
};

export default AssetTableCard;
